import type { ReactElement } from "react";
import { Link } from "react-router-dom";
import { LumiLogo } from "../../shared/components/branding/LumiLogo";

function LegalHeader(): ReactElement {
  return (
    <header className="border-b border-neutral-800">
      <div className="container mx-auto px-8 py-6">
        <Link to="/" className="inline-block">
          <LumiLogo className="h-6" ariaLabel="Lumi - Home" />
        </Link>
      </div>
    </header>
  );
}

function LegalFooter(): ReactElement {
  return (
    <div className="mt-12 pt-8 border-t border-neutral-800">
      <Link to="/" className="text-neutral-400 hover:text-white transition-colors">
        ← Back to Home
      </Link>
    </div>
  );
}

function PrivacyContent(): ReactElement {
  return (
    <div className="prose prose-invert prose-neutral max-w-none">
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">1. Information We Collect</h2>
        <p className="text-neutral-300 leading-relaxed mb-4">We collect information to provide better services:</p>
        <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
          <li>Account information: email address, password (encrypted), display name</li>
          <li>Viewing activity: what you watch, when you watch, watch progress</li>
          <li>Device information: browser type, OS, IP address, device identifiers</li>
          <li>Preferences: language, subtitle settings, genre preferences</li>
          <li>Analytics: how you interact with the Service</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">2. How We Use Your Information</h2>
        <p className="text-neutral-300 leading-relaxed mb-4">We use your information to:</p>
        <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
          <li>Provide and improve the Service</li>
          <li>Personalize your viewing experience and recommendations</li>
          <li>Resume playback where you left off</li>
          <li>Communicate with you about your account</li>
          <li>Analyze usage patterns to improve content and features</li>
          <li>Prevent fraud and ensure security</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">3. Information Sharing</h2>
        <p className="text-neutral-300 leading-relaxed">
          We do not sell your personal information. We may share information with:
        </p>
        <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4 mt-4">
          <li>Service providers who help us operate the platform</li>
          <li>Content providers for analytics purposes</li>
          <li>Law enforcement when required by law</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">4. Cookies and Tracking</h2>
        <p className="text-neutral-300 leading-relaxed">
          We use cookies and similar technologies to maintain your session, remember your preferences, and analyze how
          you use the Service. You can control cookies through your browser settings.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">5. Data Security</h2>
        <p className="text-neutral-300 leading-relaxed">
          We implement appropriate security measures to protect your information, including encryption, secure
          connections (HTTPS), and access controls. However, no system is completely secure.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">6. Your Rights</h2>
        <p className="text-neutral-300 leading-relaxed mb-4">You have the right to:</p>
        <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
          <li>Access your personal information</li>
          <li>Correct inaccurate information</li>
          <li>Delete your account and associated data</li>
          <li>Export your data</li>
          <li>Opt out of marketing communications</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">7. Children's Privacy</h2>
        <p className="text-neutral-300 leading-relaxed">
          The Service is not intended for children under 13. We do not knowingly collect personal information from
          children. If we learn we have collected such information, we will delete it promptly.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">8. International Data Transfers</h2>
        <p className="text-neutral-300 leading-relaxed">
          Your information may be transferred to and processed in countries other than your country of residence. We
          ensure appropriate safeguards are in place for such transfers.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">9. Changes to This Policy</h2>
        <p className="text-neutral-300 leading-relaxed">
          We may update this Privacy Policy from time to time. We will notify you of significant changes via email or
          through the Service. Your continued use after changes constitutes acceptance.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">10. Contact Us</h2>
        <p className="text-neutral-300 leading-relaxed">
          For questions about this Privacy Policy or to exercise your rights, contact us at:
          <br />
          <a href="mailto:privacy@lumi.theforge.space" className="text-lumi-accent hover:underline">
            privacy@lumi.theforge.space
          </a>
        </p>
      </section>
    </div>
  );
}

export function PrivacyPolicy(): ReactElement {
  return (
    <div className="min-h-screen bg-black text-white">
      <LegalHeader />

      <main className="container mx-auto px-8 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-neutral-400 mb-8">Effective Date: November 12, 2025</p>

        <PrivacyContent />
        <LegalFooter />
      </main>
    </div>
  );
}
